import { Calendar as CalendarIcon } from "lucide-react";
import React from "react";

import { isoToTyped, maskTypedDate, parseTypedDate } from "@/lib/typed-date";
import { cn } from "@/lib/utils";

/**
 * Дата с КЛАВИАТУРЫ (`дд.мм.гггг`) + календарь кнопкой рядом.
 *
 * Календарь остался нативным `<input type=date>`, но прозрачным поверх иконки:
 * `showPicker()` есть не во всех браузерах ТСД, а тап по самому полю открывает
 * календарь везде. Ошибку ввода поле показывает САМО и отдаёт наверх — родитель
 * гасит по ней кнопку, иначе неверная дата ушла бы в 1С как пустая: строка
 * «32.01.26» превратилась бы в «дата не указана» молча.
 */
export function DateInput({
  value,
  onChange,
  className,
  ariaLabel,
}: {
  /** `yyyy-mm-dd` или пусто. */
  value: string;
  /** `iso` пусто, если ввод неполон или неверен; `error` — почему. */
  onChange: (iso: string, error: string) => void;
  className?: string;
  ariaLabel?: string;
}): React.ReactElement {
  const [text, setText] = React.useState(() => isoToTyped(value));
  const [error, setError] = React.useState("");

  // Значение пришло снаружи (скан GS1 отдаёт даты из кода, календарь — из
  // пикера) — показываем его, но НЕ затираем то, что человек набирает сейчас.
  React.useEffect(() => {
    const asIso = parseTypedDate(text).iso;
    if (value !== asIso) {
      setText(isoToTyped(value));
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const apply = (raw: string) => {
    const masked = maskTypedDate(raw);
    setText(masked);
    const { iso, error: err } = parseTypedDate(masked);
    setError(err);
    onChange(iso, err);
  };

  return (
    <div className={className}>
      <div className="flex gap-1">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={10}
          placeholder="дд.мм.гггг"
          aria-label={ariaLabel}
          aria-invalid={Boolean(error)}
          className={cn(
            "w-full rounded border p-2 tabular-nums",
            error && "border-red-500 bg-red-50",
          )}
          value={text}
          onChange={(e) => apply(e.target.value)}
        />
        <div className="relative shrink-0">
          <div className="flex h-full w-10 items-center justify-center rounded border bg-white text-slate-500">
            <CalendarIcon className="size-4" />
          </div>
          <input
            type="date"
            tabIndex={-1}
            aria-label={ariaLabel ? `${ariaLabel} — календарь` : "календарь"}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            value={value}
            onChange={(e) => {
              setText(isoToTyped(e.target.value));
              setError("");
              onChange(e.target.value, "");
            }}
          />
        </div>
      </div>
      {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
    </div>
  );
}
